import {
  collection,
  getDocs,
  getCountFromServer,
  doc,
  getDoc,
  getDocFromServer,
  setDoc,
  updateDoc,
  deleteDoc,
  deleteField,
  runTransaction,
  writeBatch,
  serverTimestamp,
  query,
  where,
  orderBy,
  onSnapshot,
} from 'firebase/firestore';
import { structureFromSnap } from './structureRecord';
import { applyStructureOps } from '../data/repositories/structures.repository';
import { removeRoomElementsWhere } from '../data/repositories/rooms.repository';
import { db, auth } from '../firebase';
import { createCallable } from '../firebaseFunctions';
import { stripUndefined, assertNoUndefined } from '../utils/firestoreSafe';
import {
  buildExteriorPatch,
  applyChildSubstitution,
  applyLockUpdates,
  shiftCeilingByDelta,
  shiftSectionsByDelta,
  applyStructureFloorCorners,
} from '../utils/wallStretch';
import * as THREE from 'three';
import { exportStructureToJSON } from './preTransformedStructureService';
import { transformPolygonCorners } from '../utils/polygonWallUtils';
import {
  assembleVanity,
  VANITY_DIMENSIONS,
  VANITY_STYLE_CONFIG,
} from '@shared-lib/vanity-assembly';
import { checkPlacement } from '@shared-lib/collision';
import {
  assembleStructureWithRooms,
  normalizeRoomData,
} from '@shared-lib/rooms/structureWithRooms';
import { getDefaultGLBModel, getBestMatchingModel } from './glbModelService';
import { updateStructureArray } from '../utils/openingSyncUtils';
import { planChanges } from '../utils/openingEdit';

/**
 * Remove undefined values from an object (Firestore doesn't accept undefined)
 * @param {Object} obj - Object to clean
 * @returns {Object} Object with undefined values removed
 */
const removeUndefinedValues = (obj) => {
  if (obj === null || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) {
    return obj.map(removeUndefinedValues);
  }
  const cleaned = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = removeUndefinedValues(value);
    }
  }
  return cleaned;
};

/**
 * Format a raw GLB model record into the model data shape expected by assembleVanity.
 */
function formatModelData(model) {
  if (!model) return null;
  return {
    id: model.id,
    url: model.url || model.glbURL || model.modelUrl || null,
    name: model.name || null,
    scaleFactor: model.scaleFactor || 1,
    dimensions: model.dimensions || null,
    ...(model.scale && { scale: model.scale }),
    ...(model.centerOffset && { centerOffset: model.centerOffset }),
    ...(model['sink-cutout'] && { 'sink-cutout': model['sink-cutout'] }),
    ...(model['vessel-bottom'] && { 'vessel-bottom': model['vessel-bottom'] }),
    ...(model['faucet-mounting-points'] && {
      'faucet-mounting-points': model['faucet-mounting-points'],
    }),
  };
}

/**
 * Select all vanity models matched to a style.
 * Uses VANITY_STYLE_CONFIG to map style → model search terms,
 * then queries via getBestMatchingModel for keyword scoring.
 * Falls back to getDefaultGLBModel if no style match found.
 *
 * @param {string} [style='modern'] - Style name
 * @param {Object} [options]
 * @param {number} [options.sinkCount=1] - 1 or 2
 * @returns {Promise<Object>} All model data fields for assembleVanity
 */
export const selectModelsForVanityStyle = async (
  style = 'modern',
  options = {}
) => {
  const styleConfig = VANITY_STYLE_CONFIG[style] || VANITY_STYLE_CONFIG.modern;

  const { fallbackType, fallbackSinkType } = styleConfig.sink;
  const sinkOptions = fallbackSinkType ? { sinkType: fallbackSinkType } : {};
  const stylePrefs = { style: [style] };

  const [sinkModel, faucetModel, mirrorModel, sconceModel, overheadLightModel] =
    await Promise.all([
      getBestMatchingModel(fallbackType, stylePrefs, sinkOptions).catch(
        () => null
      ),
      getBestMatchingModel('bathroom-sink-faucet', stylePrefs).catch(
        () => null
      ),
      getBestMatchingModel('mirror', stylePrefs).catch(() => null),
      getBestMatchingModel('wall-light', stylePrefs).catch(() => null),
      getBestMatchingModel('overhead-wall-light', stylePrefs).catch(() => null),
    ]);

  return {
    sinkModelData: formatModelData(sinkModel),
    faucetModelData: formatModelData(faucetModel),
    mirrorModelData: formatModelData(mirrorModel),
    sconceModelData: formatModelData(sconceModel),
    overheadLightModelData: formatModelData(overheadLightModel),
  };
};

/**
 * Create a complete vanity with style-matched models.
 * Single entry point for all web app vanity creation.
 *
 * Runs the full cloud creation pipeline:
 *  1. Local `assembleVanity` builds the core fixtures (cabinet,
 *     countertop, sinks, faucets) against the room so we have a stable
 *     fixtureGroupId to target.
 *  2. Cloud `build_vanity` (via `executeDesignTool`) runs the unified
 *     create+optimize pipeline against the augmented room, returning
 *     the final space-aware mirror / lighting / collision-pruned objects.
 *
 * If `structureId` is missing or the cloud call fails, falls back to the
 * local assembly alone.
 *
 * @param {Object} params
 * @param {string} params.roomId
 * @param {string} [params.structureId] - required for the cloud-optimize pass
 * @param {Object} params.center - {x, y, z} pre-resolved center position
 * @param {Object} params.rotation
 * @param {number} [params.floorY]
 * @param {string[]} params.parentWallIds
 * @param {number} [params.sinkCount=1]
 * @param {string} [params.style='modern']
 * @param {number} [params.width]
 * @param {number} [params.height]
 * @param {number} [params.depth]
 * @param {Object} [params.modelOverrides]
 * @param {Object} roomData
 * @returns {Promise<{objects: Object[], cabinetRun: Object|null}>} the vanity's
 *   objects (post-optimize) and, from the cloud pass, the cabinet RUN it was
 *   built as. A caller saving the objects MUST save the run with them: they
 *   carry its id, and a run the room does not hold leaves the panel offering
 *   only Run-scope edits, which fail ("Could not apply appearance").
 */
export const createVanity = async (params, roomData) => {
  const style = params.style || 'modern';
  const styleConfig = VANITY_STYLE_CONFIG[style] || VANITY_STYLE_CONFIG.modern;
  const sinkCount = params.sinkCount || 1;

  const defaults =
    sinkCount === 2
      ? VANITY_DIMENSIONS.doubleSink
      : VANITY_DIMENSIONS.singleSink;
  const width = params.width || defaults.width;
  const height = params.height || defaults.height;
  const depth = params.depth || defaults.depth;

  const floorY =
    params.floorY !== undefined
      ? params.floorY
      : roomData?.floors?.[0]?.center?.y || 0;

  // Fetch style-matched models (or use overrides if provided)
  const overrides = params.modelOverrides || {};
  const styleModels = await selectModelsForVanityStyle(style, { sinkCount });

  const result = assembleVanity({
    roomId: params.roomId,
    position: { x: params.center.x, z: params.center.z },
    rotation: params.rotation || { x: 0, y: 0, z: 0 },
    floorY,
    parentWallIds: params.parentWallIds || [],
    sinkCount,
    dimensions: { width, height, depth },
    modelData: {
      cabinetTextureId: overrides.cabinetTextureId || null,
      countertopTextureId: overrides.countertopTextureId || null,
      sinkModelData: overrides.sinkModelData || styleModels.sinkModelData,
      faucetModelData: overrides.faucetModelData || styleModels.faucetModelData,
      mirrorModelData: overrides.mirrorModelData ?? styleModels.mirrorModelData,
      sconceModelData: overrides.sconceModelData ?? styleModels.sconceModelData,
      overheadLightModelData:
        overrides.overheadLightModelData ?? styleModels.overheadLightModelData,
    },
    finishes: {
      faucetFinish: params.faucetFinish || 'chrome',
      handleFinish: params.handleFinish || 'chrome',
    },
    roomData,
    styleConfig,
  });

  // Validate cabinet placement against room
  const cabinet = result.objects.find(
    (o) => o.type === 'lower-cabinet' || o.category?.['lower-cabinet']
  );
  if (cabinet) {
    const validation = checkPlacement(cabinet, roomData, {
      excludeGroupIds: [result.fixtureGroupId],
    });
    if (!validation.valid) {
      console.warn(
        `[createVanity] Placement issues:`,
        validation.collisions.map((c) => c.reason)
      );
    }
  }

  // Route through cloud build_vanity for space-aware mirror/lighting
  // selection, opening-collision pruning, and the fallback cascade — the
  // same composed pipeline the cloud + test harness + AI agent run. Keeps
  // web-app creation on the single source of truth rather than maintaining
  // a browser-side duplicate.
  if (params.structureId && cabinet) {
    const augmentedRoom = {
      ...roomData,
      objects: [...(roomData?.objects || []), ...result.objects],
    };
    try {
      const executeDesignTool = createCallable('executeDesignTool', {
        timeout: 120000,
      });
      const res = await executeDesignTool({
        toolName: 'build_vanity',
        params: {
          roomId: params.roomId,
          structureId: params.structureId,
          style,
          vanityId: cabinet.id,
          returnOnly: true,
          roomData: augmentedRoom,
          // The company's cabinet line (design 2026-10-05, D6).
          ...(params.cabinetFamily
            ? { cabinetFamily: params.cabinetFamily }
            : {}),
          // ...and what the company offers of it (issue #2460)
          ...(params.cabinetFamily && params.companyOffering
            ? { companyOffering: params.companyOffering }
            : {}),
        },
      });
      const builtObjects = res.data?.data?.objects;
      if (Array.isArray(builtObjects) && builtObjects.length > 0) {
        return {
          objects: builtObjects,
          cabinetRun: res.data?.data?.cabinetRun ?? null,
        };
      }
      console.warn(
        '[createVanity] build_vanity returned no objects; using local assembly'
      );
    } catch (err) {
      console.warn(
        '[createVanity] build_vanity cloud call failed; using local assembly',
        err
      );
    }
  }

  return { objects: result.objects, cabinetRun: null };
};

/**
 * Get a single structure document by ID (structure-level data only, no rooms)
 * Use this when you need structure-level walls, floors, etc.
 */
export const getStructureDocument = async (structureId) => {
  try {
    const structureRef = doc(db, 'structures', structureId);
    const structureSnap = await getDoc(structureRef);

    if (!structureSnap.exists()) {
      return null;
    }

    return structureFromSnap(structureSnap);
  } catch (error) {
    console.error('Error fetching structure document:', error);
    throw error;
  }
};

export const getStructuresByProjectId = async (projectId) => {
  try {
    const structuresCollection = collection(db, 'structures');
    const q = query(
      structuresCollection,
      where('projectId', '==', projectId),
      orderBy('createdAt', 'desc')
    );
    const structuresSnapshot = await getDocs(q);
    const structuresList = structuresSnapshot.docs.map(structureFromSnap);

    return structuresList;
  } catch (error) {
    console.error('Error fetching structures for project:', error);
    throw error;
  }
};

// ===== ROOM MODIFICATION PERSISTENCE FUNCTIONS =====

/**
 * Get a room document from rooms collection
 */
export const getRoom = async (roomId) => {
  try {
    const roomRef = doc(db, 'rooms', roomId);
    const roomSnap = await getDoc(roomRef);

    if (roomSnap.exists()) {
      return normalizeRoomData({ ...roomSnap.data(), id: roomSnap.id });
    } else {
      throw new Error(`Room ${roomId} not found`);
    }
  } catch (error) {
    console.error('Error fetching room:', error);
    throw error;
  }
};

/**
 * Get a room document, bypassing the local cache.
 *
 * `getRoom` above uses `getDoc`, which — while a `onSnapshot` listener is open
 * on the collection, as `useRoomsSync` always keeps one — is served from that
 * listener's cache and can therefore predate a write made outside this client.
 * That is exactly the read a caller wants after a CLOUD tool has written the
 * room doc server-side: the listener's copy is a round trip behind, and the
 * whole point of asking again is to get past it.
 */
export const getRoomFromServer = async (roomId) => {
  const roomSnap = await getDocFromServer(doc(db, 'rooms', roomId));
  if (!roomSnap.exists()) throw new Error(`Room ${roomId} not found`);
  return normalizeRoomData({ ...roomSnap.data(), id: roomSnap.id });
};

/**
 * Get all rooms for a specific structure
 */
export const getRoomsForStructure = async (structureId) => {
  try {
    const roomsCollection = collection(db, 'rooms');
    const q = query(roomsCollection, where('structureId', '==', structureId));
    const roomsSnapshot = await getDocs(q);

    const rooms = roomsSnapshot.docs.map((doc) =>
      normalizeRoomData({
        ...doc.data(),
        id: doc.id,
      })
    );

    return rooms;
  } catch (error) {
    console.error('Error fetching rooms for structure:', error);
    throw error;
  }
};

/**
 * Room COUNT per structure, without downloading a single room document.
 *
 * The project page renders an "N rooms" badge on every scan card. It used to
 * get that number by fetching every room doc for every scan and calling
 * `.filter().length` — on `819 Montrose Ave` that is 63 documents / 2,952 KB
 * to render eight numbers. Firestore's count aggregation answers it server-side
 * and transfers no documents (billed at roughly one read per 1000 counted).
 *
 * The structure doc cannot answer this: no structure carries a `roomCount`
 * field at all, and `roomIds.length` disagrees with reality on ~4% of dev
 * structures — mostly `scanSource: 'floorPlan'`, where it is badly wrong
 * (roomIds 15 vs 1 room) — so it would make the badge lie.
 *
 * One query PER structure id, deliberately not an `in` batch: the `rooms` read
 * rule authorizes each room through chained get()s (room -> structure ->
 * project), Firestore caps get()/exists() at 10 per request, and an `in` is
 * evaluated against every value — so a project with more than ~3 scans is
 * denied outright. Same reason `getRoomsForMultipleStructures` queries per id.
 *
 * A count that fails resolves to `null` rather than sinking its siblings, so
 * one unreadable scan costs its own badge and nothing else.
 *
 * @param {Array<string>} structureIds
 * @returns {Promise<Object<string, number|null>>} structureId -> count
 */
export const getRoomCountsForStructures = async (structureIds) => {
  const ids = [...new Set((structureIds || []).filter(Boolean))];
  if (ids.length === 0) return {};

  const roomsCollection = collection(db, 'rooms');
  const counts = await Promise.all(
    ids.map((structureId) =>
      getCountFromServer(
        query(roomsCollection, where('structureId', '==', structureId))
      )
        .then((snap) => snap.data().count)
        .catch((error) => {
          console.error(
            `Error counting rooms for structure ${structureId}:`,
            error
          );
          return null;
        })
    )
  );

  return Object.fromEntries(ids.map((id, i) => [id, counts[i]]));
};

/**
 * Update wall texture information for a specific wall in a room
 */
export const updateStructure = async (structureId, updateData) => {
  try {
    const structureRef = doc(db, 'structures', structureId);
    await updateDoc(
      structureRef,
      stripUndefined({
        ...updateData,
        lastModified: serverTimestamp(),
      })
    );

    console.log('Structure updated successfully:', structureId);
    return { success: true };
  } catch (error) {
    console.error('Error updating structure:', error);
    throw error;
  }
};

/**
 * Update the texture on an exterior wall (or all exterior walls).
 *
 * Mirrors updateRoomWallTexture but writes to the structure-level
 * `exterior.walls` array instead of a room doc. Exterior walls live at
 * structure.exterior.walls and are rebuilt on every processStructure
 * run, so this texture is intentionally transient across reprocesses —
 * same lifecycle as interior wall textures stored on room.walls.
 *
 * @param {string} structureId
 * @param {string|null} exteriorWallId - Specific wall id, or null to
 *   apply to every exterior wall in a single document update.
 * @param {Object} textureData - Schema from createWallTextureModification
 *   (i.e. { regions: [...] }).
 */
export const updateExteriorWallTexture = async (
  structureId,
  exteriorWallId,
  textureData
) => {
  try {
    const structureRef = doc(db, 'structures', structureId);
    const snap = await getDoc(structureRef);

    if (!snap.exists()) {
      throw new Error(`Structure ${structureId} not found`);
    }

    const exterior = snap.data()?.exterior;
    const existingWalls = Array.isArray(exterior?.walls) ? exterior.walls : [];

    if (existingWalls.length === 0) {
      throw new Error(
        `Structure ${structureId} has no exterior walls to update`
      );
    }

    const stamp = new Date().toISOString();
    const updatedWalls = existingWalls
      .filter((w) => w)
      .map((w) => {
        const isMatch = exteriorWallId == null || w.id === exteriorWallId;
        if (!isMatch) return w;
        return {
          ...w,
          texture: textureData,
          lastModified: stamp,
        };
      });

    await updateDoc(
      structureRef,
      stripUndefined({
        'exterior.walls': updatedWalls,
        lastModified: serverTimestamp(),
      })
    );

    return { success: true };
  } catch (error) {
    console.error('Error updating exterior wall texture:', error);
    throw error;
  }
};

/**
 * Apply a wall-stretch plan to Firestore in a single multi-doc transaction.
 *
 * Touches structures/{structureId} and every rooms/{roomId} doc whose room
 * appears in the plan (cascaded room walls, polygon corners, or moved
 * children). Mirrors the cross-doc transaction shape of `useRoomDrawing.js`,
 * NOT the single-doc shape of the structures session's write.
 *
 * The plan is pure data — verify the plan's references still exist against
 * the freshly-read docs, then splice each doc's arrays directly from the
 * plan's payload via the shared helpers in `wallStretch`. The full
 * substitution lives in `applyStretchPlan`; the helpers used here are the
 * same ones it calls, so preview and persist cannot drift.
 *
 * Returns `{ success, newStructureData, newRoomsData }`. The caller pushes
 * `newStructureData` into `setStructureDataFromCollection` and
 * `newRoomsData` into `useRoomsSync.applyExternalUpdate` so local state
 * mirrors what was written.
 *
 * @param {string} structureId
 * @param {Object} plan - StretchPlan from `wallStretch.computeStretch`.
 * @param {string[]} affectedRoomIds - Pre-computed list (caller derives from
 *   the plan) so the transaction reads exactly the rooms it will write.
 */
/**
 * Persist a room's ceiling-edit spec and regenerate its ceiling.
 *
 * Ceiling edits live on the STRUCTURE doc keyed by room id
 * (`structure.ceilingEdits[roomId]`, ceiling-editing decision #9) so they
 * outlive a rewrite of the room docs (a merge, a split). A reprocess rewrites
 * the structure doc too and clears them, as it clears every user edit (user
 * ruling 2026-10-23). The heavy geometry — floor
 * partitioning + wall-top propagation — runs cloud-side in
 * `regenerateCeilings`, which re-reads the structure, re-applies the spec,
 * and writes each edited room's `ceilings` (and `walls`). So the client
 * only writes the one structure field and fires the callable.
 *
 * @param {string} structureId
 * @param {string} roomId
 * @param {object|null} spec - from buildCeilingEditSpec; null clears the
 *   room's edits (falls back to the auto partition).
 * @returns {Promise<object>} the regenerateCeilings result.
 */
export const applyCeilingEditToFirestore = async (
  structureId,
  roomId,
  spec
) => {
  if (!structureId || !roomId) {
    throw new Error(
      'applyCeilingEditToFirestore: missing structureId or roomId'
    );
  }
  const structureRef = doc(db, 'structures', structureId);
  // Dot-path update touches only this room's key — other rooms' edits and
  // unrelated structure fields are preserved (last-writer-wins per field).
  await updateDoc(structureRef, {
    [`ceilingEdits.${roomId}`]: spec ? stripUndefined(spec) : deleteField(),
  });

  const regenerateCeilings = createCallable('regenerateCeilings');
  const { data } = await regenerateCeilings({
    structureId,
    roomIds: [roomId],
  });
  return data;
};

export const regenerateCeilingsForRooms = async (structureId, roomIds) => {
  const ids = [...new Set((roomIds || []).filter(Boolean))];
  if (!structureId || ids.length === 0) return [];
  const regenerateCeilings = createCallable('regenerateCeilings');
  const { data } = await regenerateCeilings({ structureId, roomIds: ids });
  return data?.updates || [];
};

export const applyStretchPlanToFirestore = async (
  structureId,
  plan,
  affectedRoomIds
) => {
  if (!plan) {
    throw new Error('applyStretchPlanToFirestore: missing plan');
  }

  const CHILD_ARRAYS = ['doors', 'windows', 'openings', 'objects'];

  const structureRef = doc(db, 'structures', structureId);
  const roomRefs = (affectedRoomIds || []).map((rid) => ({
    rid,
    ref: doc(db, 'rooms', rid),
  }));

  // Read phase — non-transactional. Phase 1b traded the transaction's
  // atomic read-and-write contract for a single-RTT WriteBatch commit:
  // the verify pass below catches pre-confirm structure drift, and
  // field-level updates mean a concurrent writer touching unrelated
  // fields between our read and our commit is preserved (last-writer-
  // wins per touched field). True atomicity isn't available via the
  // Firestore JS SDK without `runTransaction`, so document this if we
  // ever need to revert.
  const structureSnap = await getDoc(structureRef);
  if (!structureSnap.exists()) {
    throw new Error(`Structure ${structureId} not found`);
  }
  const roomSnaps = await Promise.all(roomRefs.map(({ ref }) => getDoc(ref)));

  const structureFields = structureSnap.data();
  const rooms = roomSnaps
    .map((snap, i) => {
      if (!snap.exists()) return null;
      return { ...snap.data(), id: roomRefs[i].rid };
    })
    .filter(Boolean);

  // Verify the plan's references still exist. If a wall/room got renamed
  // or removed between preview and confirm, abort cleanly.
  for (const entry of plan.affectedStructureWalls || []) {
    const wall = (structureFields.walls || []).find(
      (w) => w && w.id === entry.wallId
    );
    if (!wall) {
      throw new Error(`Wall ${entry.wallId} no longer present — please retry`);
    }
  }
  for (const entry of plan.derivedRoomWalls || []) {
    const room = rooms.find((r) => r.id === entry.roomId);
    if (!room) {
      throw new Error(`Room ${entry.roomId} no longer present — please retry`);
    }
    const exists = (room.walls || []).some((w) => w && w.id === entry.wallId);
    if (!exists) {
      throw new Error(
        `Room wall ${entry.wallId} no longer present — please retry`
      );
    }
  }

  // Build lookup maps from the plan payload.
  const newWallById = new Map();
  for (const entry of plan.affectedStructureWalls || []) {
    newWallById.set(entry.wallId, entry.after);
  }
  const newRoomWallByKey = new Map();
  for (const entry of plan.derivedRoomWalls || []) {
    newRoomWallByKey.set(`${entry.roomId}:${entry.wallId}`, entry.after);
  }
  const newCornerByKey = new Map();
  for (const entry of plan.affectedPolygonCorners || []) {
    newCornerByKey.set(`${entry.roomId}:${entry.cornerIndex}`, entry.after);
  }

  // --- Splice room docs ---
  const nextRooms = rooms.map((room) => {
    const next = { ...room };
    next.walls = (room.walls || []).map((wall) => {
      const k = `${room.id}:${wall.id}`;
      return newRoomWallByKey.has(k) ? newRoomWallByKey.get(k) : wall;
    });
    if (room.floors && room.floors.length > 0) {
      next.floors = room.floors.map((floor, fi) => {
        if (fi !== 0 || !floor.polygonCorners) return floor;
        return {
          ...floor,
          polygonCorners: floor.polygonCorners.map((corner, idx) => {
            const k = `${room.id}:${idx}`;
            if (!newCornerByKey.has(k)) return corner;
            const after = newCornerByKey.get(k);
            if (Array.isArray(corner)) return [after.x, after.z];
            return { ...corner, x: after.x, z: after.z };
          }),
        };
      });
    }
    for (const arrayName of CHILD_ARRAYS) {
      if (!room[arrayName]) continue;
      next[arrayName] = applyChildSubstitution(
        room[arrayName],
        arrayName,
        plan,
        { scope: 'room', roomId: room.id }
      );
    }
    // Translate rooms ride along with the wall/corner shift — apply the
    // XZ shift to their ceilings inline so we don't have to round-trip
    // through regenerateCeilings for rooms whose topology didn't change.
    // Same goes for `room.center` and `section.center` (label positions):
    // nothing else in the cascade touches them, so labels would otherwise
    // stay pinned at the pre-stretch position.
    if (plan.roomClassification?.translate?.has(room.id)) {
      if (room.ceilings) {
        next.ceilings = shiftCeilingByDelta(
          room.ceilings,
          plan.delta,
          plan.direction
        );
      }
      if (room.center) {
        const dx = plan.delta * plan.direction.x;
        const dz = plan.delta * plan.direction.z;
        next.center = {
          ...room.center,
          x: (room.center.x || 0) + dx,
          z: (room.center.z || 0) + dz,
        };
      }
      if (Array.isArray(room.sections)) {
        next.sections = shiftSectionsByDelta(
          room.sections,
          plan.delta,
          plan.direction
        );
      }
      // floors[].center.x/z drives fixtureTextureRegionUtils' floor-bbox;
      // polygonCorners were already shifted above into next.floors via
      // affectedPolygonCorners, so chain off next.floors when present.
      const floorsSrc = Array.isArray(next.floors) ? next.floors : room.floors;
      if (Array.isArray(floorsSrc) && floorsSrc.length > 0) {
        const dx = plan.delta * plan.direction.x;
        const dz = plan.delta * plan.direction.z;
        next.floors = floorsSrc.map((floor) => {
          if (!floor || !floor.center) return floor;
          return {
            ...floor,
            center: {
              ...floor.center,
              x: (floor.center.x || 0) + dx,
              z: (floor.center.z || 0) + dz,
            },
          };
        });
      }
    }
    return next;
  });

  // --- Splice structure doc ---
  const nextStructureWalls = (structureFields.walls || []).map((wall) =>
    newWallById.has(wall.id) ? newWallById.get(wall.id) : wall
  );

  const nextStructureChildren = {};
  for (const arrayName of CHILD_ARRAYS) {
    if (!structureFields[arrayName]) continue;
    nextStructureChildren[arrayName] = applyChildSubstitution(
      structureFields[arrayName],
      arrayName,
      plan,
      { scope: 'structure' }
    );
  }

  const exteriorPatch = buildExteriorPatch(structureFields.exterior, plan);

  // Splice `structure.floors[]` per `affectedStructureFloorCorners` so the
  // IFC/DXF exporters (which read `structure.floors` directly when
  // source = 'structure' or 'both') don't emit a stale floor plate. No-op
  // when the plan didn't touch any structure-floor corner.
  let nextStructureFloors;
  if (Array.isArray(structureFields.floors)) {
    const candidate = applyStructureFloorCorners(structureFields.floors, plan);
    if (candidate !== structureFields.floors) {
      nextStructureFloors = candidate;
    }
  }

  // Build the post-plan structure view so applyLockUpdates can stamp
  // distances against post-splice walls (not the pre-stretch shape).
  const postPlanStructure = {
    ...structureFields,
    walls: nextStructureWalls,
    rooms: nextRooms,
    ...nextStructureChildren,
    exterior:
      exteriorPatch !== undefined ? exteriorPatch : structureFields.exterior,
    ...(nextStructureFloors !== undefined
      ? { floors: nextStructureFloors }
      : {}),
  };
  const nextLocks = applyLockUpdates(
    structureFields.measurementLocks,
    plan,
    postPlanStructure
  );

  const structurePatch = {
    walls: nextStructureWalls,
    measurementLocks: nextLocks,
  };
  for (const arrayName of CHILD_ARRAYS) {
    if (arrayName in nextStructureChildren) {
      structurePatch[arrayName] = nextStructureChildren[arrayName];
    }
  }
  if (exteriorPatch !== undefined) {
    structurePatch.exterior = exteriorPatch;
  }
  if (nextStructureFloors !== undefined) {
    structurePatch.floors = nextStructureFloors;
  }

  // Plan-derived payloads are built from Firestore-read data via pure
  // splices, so an undefined leaf is a bug, not routine. assertNoUndefined
  // is loud in dev/Jest/CI and a no-op in production builds.
  assertNoUndefined(structurePatch, 'applyStretchPlanToFirestore:structure');

  // Commit phase — single batched RTT. Atomic at the batch level
  // (all-or-nothing) but doesn't reread; the verify pass above is the
  // only check that the cited references still exist.
  const batch = writeBatch(db);
  batch.update(structureRef, {
    ...structurePatch,
    lastModified: serverTimestamp(),
    modifiedBy: 'user',
  });

  for (const room of nextRooms) {
    const ref = roomRefs.find((r) => r.rid === room.id)?.ref;
    if (!ref) continue;
    const { id, ...roomFields } = room;
    assertNoUndefined(
      roomFields,
      `applyStretchPlanToFirestore:room:${room.id}`
    );
    batch.update(ref, {
      ...roomFields,
      lastModified: serverTimestamp(),
    });
  }

  await batch.commit();

  const newStructureData = {
    ...structureFields,
    walls: nextStructureWalls,
    measurementLocks: nextLocks,
    rooms: nextRooms,
    ...nextStructureChildren,
    exterior:
      exteriorPatch !== undefined ? exteriorPatch : structureFields.exterior,
  };
  const newRoomsData = new Map();
  for (const room of nextRooms) {
    newRoomsData.set(room.id, room);
  }

  return { success: true, newStructureData, newRoomsData };
};

export const applyOpeningEditToFirestore = async (
  structureId,
  plan,
  { affectedRoomIds = [], lock = null } = {}
) => {
  if (!plan) throw new Error('applyOpeningEditToFirestore: missing plan');
  if (plan.errors?.length) {
    throw new Error('applyOpeningEditToFirestore: plan has blocking errors');
  }
  const arrayKeyForKind = (kind) =>
    kind === 'door' ? 'doors' : kind === 'window' ? 'windows' : 'openings';

  // One or more openings (inter-gap edits move/resize two). Group their `after`
  // states by structure id, and note which array each lives in.
  const changes = planChanges(plan);
  const afterBySid = new Map(); // sid → after { center, dimensions }
  const keyBySid = new Map(); // sid → array key
  const affectedKeys = new Set();
  for (const c of changes) {
    const key = arrayKeyForKind(c.kind);
    afterBySid.set(c.structureOpeningId, c.after);
    keyBySid.set(c.structureOpeningId, key);
    affectedKeys.add(key);
  }

  const structureRef = doc(db, 'structures', structureId);
  const roomRefs = (affectedRoomIds || []).map((rid) => ({
    rid,
    ref: doc(db, 'rooms', rid),
  }));

  // Read phase — non-transactional, mirroring applyStretchPlanToFirestore:
  // field-level updates + a present-reference verify trade atomic read-write
  // for a single-RTT batch commit.
  const structureSnap = await getDoc(structureRef);
  if (!structureSnap.exists()) {
    throw new Error(`Structure ${structureId} not found`);
  }
  const structureFields = structureSnap.data();

  // Canonical structure write: fold every change into its array.
  const nextArrays = {};
  for (const key of affectedKeys) {
    let arr = structureFields[key] || [];
    for (const [sid, after] of afterBySid) {
      if (keyBySid.get(sid) !== key) continue;
      if (!arr.some((o) => o?.id === sid)) {
        throw new Error(`Opening ${sid} no longer present — please retry`);
      }
      arr = updateStructureArray(arr, sid, (entry) => ({
        ...entry,
        center: after.center,
        dimensions: after.dimensions,
      }));
    }
    nextArrays[key] = arr;
  }

  const roomSnaps = await Promise.all(roomRefs.map(({ ref }) => getDoc(ref)));
  const rooms = roomSnaps
    .map((snap, i) =>
      snap.exists() ? { ...snap.data(), id: roomRefs[i].rid } : null
    )
    .filter(Boolean);

  // Migration coexistence: update only room copies that still carry geometry,
  // across every affected array and every changed opening.
  const nextRoomsById = new Map();
  for (const room of rooms) {
    let roomTouched = false;
    const roomPatch = {};
    for (const key of affectedKeys) {
      const copies = room[key] || [];
      let touched = false;
      const nextCopies = copies.map((copy) => {
        const after = afterBySid.get(copy?.structureOpeningId);
        if (!after) return copy;
        const carriesGeometry =
          copy.center !== undefined || copy.dimensions !== undefined;
        if (!carriesGeometry) return copy;
        touched = true;
        const next = { ...copy };
        if (copy.center !== undefined) next.center = after.center;
        if (copy.dimensions !== undefined) next.dimensions = after.dimensions;
        return next;
      });
      if (touched) {
        roomPatch[key] = nextCopies;
        roomTouched = true;
      }
    }
    if (roomTouched) nextRoomsById.set(room.id, { ...room, ...roomPatch });
  }

  // Auto-lock the edited measurement (parity with the wall-length stretch path,
  // which auto-locks on confirm). Opening locks share the structure's
  // measurementLocks map; key/value come from the caller (width for a resize,
  // gap for a reposition / inter-gap). Folded into the same batch so geometry +
  // lock commit atomically.
  let nextLocks = null;
  if (lock?.key && typeof lock.value === 'number') {
    nextLocks = {
      ...(structureFields.measurementLocks || {}),
      [lock.key]: lock.value,
    };
  }

  // Commit phase — structure + touched room copies, one batch.
  const batch = writeBatch(db);
  const structurePatch = { ...nextArrays };
  if (nextLocks) structurePatch.measurementLocks = nextLocks;
  assertNoUndefined(structurePatch, 'applyOpeningEditToFirestore:structure');
  batch.update(structureRef, {
    ...structurePatch,
    lastModified: serverTimestamp(),
    modifiedBy: 'user',
  });
  for (const [rid, room] of nextRoomsById) {
    const ref = roomRefs.find((r) => r.rid === rid)?.ref;
    if (!ref) continue;
    const roomPatch = {};
    for (const key of affectedKeys) {
      if (room[key]) roomPatch[key] = room[key];
    }
    assertNoUndefined(roomPatch, `applyOpeningEditToFirestore:room:${rid}`);
    batch.update(ref, { ...roomPatch, lastModified: serverTimestamp() });
  }
  await batch.commit();

  const newStructureData = { ...structureFields, ...nextArrays };
  if (nextLocks) newStructureData.measurementLocks = nextLocks;
  const newRoomsData = new Map();
  for (const [rid, room] of nextRoomsById) {
    newRoomsData.set(rid, room);
  }
  return { success: true, newStructureData, newRoomsData };
};

/**
 * Remove elements from one of a room's child arrays, in a transaction against
 * whatever the server holds now.
 *
 * The floor plan's delete used to send the WHOLE array instead
 * (`updateRoomChildElements`, since deleted — a whole-array room writer has no
 * caller that is not this bug), filtered from its own copy of the room — a
 * copy taken when the click happened and written after a network round trip. Anything deleted from that room in the meantime
 * (or before, while that copy was stale) went back into Firestore with it,
 * and the room store picked it up from the next snapshot: deleted furniture
 * reappearing a moment later. Stating the removal instead of the result leaves
 * every other element exactly as the server has it — the rule
 * `updateStructureOpeningOps` follows for the structure's opening arrays.
 *
 * @param {string} roomId
 * @param {'objects'|'doors'|'windows'|'openings'} field the room array.
 * @param {(element: Object) => boolean} shouldRemove
 * @returns {Promise<number>} how many elements were removed.
 */
export const removeRoomChildElements = (roomId, field, shouldRemove) =>
  removeRoomElementsWhere(roomId, field, shouldRemove);

/**
 * Update wall geometry in structure (for structure-level walls)
 */
/**
 * Apply one geometry/miter update to a wall, returning the updated wall.
 * Shared by updateStructureWallGeometry (single) and updateStructureWallsGeometry
 * (batched) so the validation + miter handling can't drift between them.
 */
function applyWallGeometryUpdate(wall, geometryData) {
  const validCenter = geometryData.center ||
    wall.center || { x: 0, y: 0, z: 0 };
  const validDimensions = geometryData.dimensions ||
    wall.dimensions || { x: 1, y: 2.4, z: 0.1 };
  const validRotation = geometryData.rotation ||
    wall.rotation || { x: 0, y: 0, z: 0 };

  const updated = {
    ...wall,
    center: {
      x: validCenter.x ?? 0,
      y: validCenter.y ?? 0,
      z: validCenter.z ?? 0,
    },
    dimensions: {
      x: validDimensions.x ?? 1,
      y: validDimensions.y ?? 2.4,
      z: validDimensions.z ?? 0.1,
    },
    rotation: {
      x: validRotation.x ?? 0,
      y: validRotation.y ?? 0,
      z: validRotation.z ?? 0,
    },
    isWallEdited: geometryData.isWallEdited ?? wall.isWallEdited ?? false,
    geometryLastModified: new Date().toISOString(),
  };

  // Corner-adjust callers (vertex move/add/delete) pass miter fields
  // explicitly — apply them so oblique mitres survive a reload. Only touch a
  // field when the caller includes its key, so other callers (single-wall
  // move) don't clobber existing miters. Coalesce undefined → null so a corner
  // that lost its miter clears cleanly and Firestore doesn't choke on undefined.
  // A wall's elevation outline is world-space, so it has to travel with the
  // geometry — several readers prefer it to center/rotation, and a stale one
  // renders the wall (and seats its openings) at the pre-edit position.
  if ('polygonCorners' in geometryData) {
    updated.polygonCorners = geometryData.polygonCorners ?? null;
  }
  if ('leftMiter' in geometryData) {
    updated.leftMiter = geometryData.leftMiter ?? null;
  }
  if ('rightMiter' in geometryData) {
    updated.rightMiter = geometryData.rightMiter ?? null;
  }
  if ('frontFacesSameDirection' in geometryData) {
    updated.frontFacesSameDirection =
      geometryData.frontFacesSameDirection ?? null;
  }
  return updated;
}

/**
 * Apply ELEMENT operations to the structure's opening arrays (and `walls`), in
 * a transaction against whatever the server holds now.
 *
 * The viewer does not keep the scan document live, so a session's copy of
 * `doors` / `windows` / `openings` goes stale the moment anyone else edits.
 * Writing an operation instead of an array means an untouched element is left
 * exactly as the server has it, and an element deleted elsewhere is never
 * re-created. See `utils/openingArrayOps` for the rules.
 *
 * There was a whole-array writer beside this, `updateStructureChildElements`,
 * kept for the floor plan's wall edits. Each of those sent every opening (one
 * of them every wall) from the session's copy, so a wall drag put back doors
 * and windows deleted elsewhere since the page loaded. They state their
 * operations now, and it is gone.
 *
 * @param {string} structureId
 * @param {Object} opsByKey `{ walls?, doors?, windows?, openings? }`, each
 *   `{ upsert?: Array, add?: Array, remove?: string[] }`.
 * @returns {Promise<{skipped: string[]}>} ids this edit targeted that the
 *   server no longer has, as `key:id`.
 */
export const updateStructureOpeningOps = async (structureId, opsByKey) => {
  // The write lives in the data layer now: the `structures` repository
  // routes it through the open session when a page holds the document
  // (optimistic, with the field marked pending so a mid-flight snapshot
  // cannot revert it) and straight to the adapter's transaction when none
  // does. This is the legacy door the existing call sites still come
  // through, and it keeps their contract.
  try {
    return await applyStructureOps(structureId, opsByKey);
  } catch (error) {
    console.error('Error applying opening ops to structure:', error);
    throw error;
  }
};

/**
 * Delete a sink from a countertop
 * @param {string} roomId - ID of the room containing the countertop
 * @param {string} countertopId - ID of the countertop containing the sink
 * @param {string} sinkId - ID of the sink to delete
 */
export const deleteSinkFromCountertop = async (
  roomId,
  countertopId,
  sinkId
) => {
  try {
    const roomRef = doc(db, 'rooms', roomId);
    const roomSnap = await getDoc(roomRef);

    if (!roomSnap.exists()) {
      throw new Error(`Room ${roomId} not found`);
    }

    const roomData = roomSnap.data();
    const objects = roomData.objects || [];

    // Find the countertop and remove the sink from its sinks array
    const updatedObjects = objects.map((obj) => {
      if (obj.id === countertopId) {
        // This is the countertop - remove the sink
        const updatedSinks = (obj.sinks || []).filter(
          (sink) => sink.id !== sinkId
        );
        return { ...obj, sinks: updatedSinks };
      }
      return obj;
    });

    // Update the room document
    await updateDoc(roomRef, {
      objects: updatedObjects,
      lastModified: serverTimestamp(),
      modifiedBy: 'user',
    });

    return { success: true, deletedSinkId: sinkId };
  } catch (error) {
    console.error('Error deleting sink from countertop:', error);
    throw error;
  }
};

// saveRoomModifications function removed - no longer needed with direct property storage

/**
 * Per-room serialization for wall writes.
 *
 * Every measurement write is a read-modify-write of the room's WHOLE `walls`
 * array, so two that overlap each read the same snapshot and the second
 * silently discards the first. That was one root cause behind two symptoms:
 * "Restore All Measurements" issued one call per entry and kept only the last,
 * and "Reset All to Original" fired the removed-list clear and the offset
 * clear as a `Promise.all` pair, so whichever landed second put the other's
 * field back — it logged success and the entries survived a reload.
 *
 * Chained off the previous write's SETTLEMENT, so one failure doesn't wedge
 * the queue for the rest of the session. Keyed by room id; the entry is
 * dropped once it is the tail and has settled, so the map doesn't grow.
 */
const roomWallWriteQueues = new Map();

const queueRoomWallWrite = (roomId, task) => {
  const previous = roomWallWriteQueues.get(roomId) || Promise.resolve();
  const next = previous.then(task, task);
  roomWallWriteQueues.set(roomId, next);
  next.then(
    () => {
      if (roomWallWriteQueues.get(roomId) === next) {
        roomWallWriteQueues.delete(roomId);
      }
    },
    () => {
      if (roomWallWriteQueues.get(roomId) === next) {
        roomWallWriteQueues.delete(roomId);
      }
    }
  );
  return next;
};

/**
 * Apply a pure update to ONE wall of a room and persist the room doc.
 *
 * The single read-modify-write behind every per-wall measurement write —
 * `updateWall` receives the wall AS STORED and returns its replacement, so
 * callers derive the new value from the document rather than from a client
 * snapshot that a concurrent write may already have invalidated. Serialized
 * per room (see `queueRoomWallWrite`).
 *
 * @param {string} roomId
 * @param {string} wallId
 * @param {(wall: object) => object} updateWall  pure; must return a new wall
 * @returns {Promise<{success: boolean}>}
 */
export const updateRoomWall = (roomId, wallId, updateWall) =>
  queueRoomWallWrite(roomId, async () => {
    const roomRef = doc(db, 'rooms', roomId);
    const roomSnap = await getDoc(roomRef);

    if (!roomSnap.exists()) {
      throw new Error(`Room ${roomId} not found`);
    }

    const roomData = roomSnap.data();
    const walls = roomData.walls || [];

    // Not one of the room's own walls: an elevation drawn on a wall the app
    // SYNTHESIZES — an island / peninsula face (see
    // utils/elevationFreestandingWalls). Its per-elevation state lives in its
    // own map keyed by the same id, because there is no wall to carry it and
    // mapping over `walls` would match nothing and silently write the array
    // back unchanged. Everything else about it is identical: same updater,
    // same fields, same read path.
    if (!walls.some((wall) => wall?.id === wallId)) {
      const existing = roomData.virtualElevations?.[wallId] || { id: wallId };
      await updateDoc(roomRef, {
        // Whole map, not a dotted field path: the id is composed from a
        // cabinet-run id, and a dot anywhere in it would be read as a path
        // separator. The write is already serialized by queueRoomWallWrite.
        virtualElevations: {
          ...(roomData.virtualElevations || {}),
          [wallId]: {
            ...updateWall(existing),
            id: wallId,
            lastModified: new Date().toISOString(),
          },
        },
        lastModified: serverTimestamp(),
        modifiedBy: auth.currentUser?.uid || 'anonymous',
      });
      return { success: true };
    }

    const updatedWalls = walls
      .filter((wall) => wall !== null && wall !== undefined)
      .map((wall) =>
        wall.id === wallId
          ? { ...updateWall(wall), lastModified: new Date().toISOString() }
          : wall
      );

    await updateDoc(roomRef, {
      walls: updatedWalls,
      lastModified: serverTimestamp(),
      modifiedBy: auth.currentUser?.uid || 'anonymous',
    });

    return { success: true };
  });

/**
 * Get all removed measurements for a room (all walls at once)
 */
export const getRemovedMeasurementsForRoom = async (roomId) => {
  try {
    const roomRef = doc(db, 'rooms', roomId);
    const roomSnap = await getDoc(roomRef);

    if (!roomSnap.exists()) {
      return {};
    }

    const roomData = roomSnap.data();
    const walls = roomData.walls || [];
    const removedMeasurementsMap = {};

    walls.forEach((wall) => {
      if (wall && wall.id && wall.removedMeasurements?.length > 0) {
        removedMeasurementsMap[wall.id] = wall.removedMeasurements;
      }
    });
    // Synthesized elevations (island / peninsula faces) keep the same state
    // under their own map — see updateRoomWall.
    Object.entries(roomData.virtualElevations || {}).forEach(([id, entry]) => {
      if (entry?.removedMeasurements?.length > 0) {
        removedMeasurementsMap[id] = entry.removedMeasurements;
      }
    });

    return removedMeasurementsMap;
  } catch (error) {
    console.error('Error getting removed measurements for room:', error);
    return {};
  }
};

/**
 * Merge one dragged offset into a wall's saved offsets.
 */
export const saveMeasurementOffset = (roomId, wallId, measurementKey, offset) =>
  updateRoomWall(roomId, wallId, (wall) => ({
    ...wall,
    measurementOffsets: {
      ...(wall.measurementOffsets || {}),
      [measurementKey]: offset,
    },
  }));

/**
 * Clear BOTH of a wall's measurement customizations in one write — the
 * "Reset All to Original" action. Two separate writes would race each other
 * (see `queueRoomWallWrite`); one write cannot.
 */
export const resetWallMeasurementCustomization = (roomId, wallId) =>
  updateRoomWall(roomId, wallId, (wall) => ({
    ...wall,
    removedMeasurements: [],
    measurementOffsets: {},
  }));

/**
 * Get all measurement offsets for a room (all walls at once)
 */
export const getMeasurementOffsetsForRoom = async (roomId) => {
  try {
    const roomRef = doc(db, 'rooms', roomId);
    const roomSnap = await getDoc(roomRef);

    if (!roomSnap.exists()) {
      return {};
    }

    const roomData = roomSnap.data();
    const walls = roomData.walls || [];
    const offsetsMap = {};

    walls.forEach((wall) => {
      if (wall && wall.id && wall.measurementOffsets) {
        offsetsMap[wall.id] = wall.measurementOffsets;
      }
    });
    Object.entries(roomData.virtualElevations || {}).forEach(([id, entry]) => {
      if (entry?.measurementOffsets) {
        offsetsMap[id] = entry.measurementOffsets;
      }
    });

    return offsetsMap;
  } catch (error) {
    console.error('Error getting measurement offsets for room:', error);
    return {};
  }
};

/**
 * Apply bathroom layout via unified cloud function (V2).
 * Drop-in replacement for updateRoomBathroomFixtures — same signature.
 * Calls apply_bathroom_layout_v2 on the server instead of doing client-side processing.
 */
export const applyBathroomLayoutV2 = async (
  roomId,
  fixtures,
  stylePreferences = null,
  { cabinetFamily = null, companyOffering = null } = {}
) => {
  // Look up structureId from room doc (callers don't always have it)
  const roomSnap = await getDoc(doc(db, 'rooms', roomId));
  if (!roomSnap.exists()) {
    throw new Error(`Room ${roomId} not found`);
  }
  const structureId = roomSnap.data().structureId;
  if (!structureId) {
    throw new Error(`Room ${roomId} has no structureId`);
  }

  const style =
    typeof stylePreferences === 'string'
      ? stylePreferences
      : stylePreferences?.style?.[0] || 'modern';
  const colorPalette = stylePreferences?.color?.[0] || null;

  const executeV2 = createCallable('executeDesignTool', {
    timeout: 480000,
  });
  const result = await executeV2({
    toolName: 'apply_bathroom_layout_v2',
    params: {
      roomId,
      structureId,
      layout: { fixtures },
      style,
      colorPalette,
      // The company's cabinet line (design 2026-10-05, D6): the vanities
      // the layout builds are built in it.
      ...(cabinetFamily ? { cabinetFamily } : {}),
      // ...and what the company offers of it (issue #2460)
      ...(cabinetFamily && companyOffering ? { companyOffering } : {}),
    },
  });

  if (!result.data?.success) {
    const errorMsg = result.data?.message || 'Failed to apply bathroom layout';
    console.error(
      '[applyBathroomLayoutV2]',
      errorMsg,
      result.data?.data?.errors
    );
    throw new Error(errorMsg);
  }

  return result.data;
};

/**
 * Get image enhancements for multiple structure IDs
 */
const ENHANCEMENT_QUERY_CHUNK = 8;

export const getImageEnhancementsForStructures = async (structureIds) => {
  try {
    if (!structureIds || structureIds.length === 0) {
      return [];
    }

    // The rules check each rendering's structure (and its project) per
    // `structureId` value in the query, and a request may make only 10 such
    // lookups — so ask for at most 8 structures at a time.
    const enhancementsRef = collection(db, 'image-enhancements');
    const chunks = [];
    for (let i = 0; i < structureIds.length; i += ENHANCEMENT_QUERY_CHUNK) {
      chunks.push(structureIds.slice(i, i + ENHANCEMENT_QUERY_CHUNK));
    }
    const snapshots = await Promise.all(
      chunks.map((ids) =>
        getDocs(
          query(
            enhancementsRef,
            where('structureId', 'in', ids),
            orderBy('createdAt', 'desc')
          )
        )
      )
    );
    const enhancements = [];

    snapshots
      .flatMap((snap) => snap.docs)
      .forEach((doc) => {
        const data = doc.data();
        const sessionId = doc.id;

        // Extract all enhanced images from this session
        if (data.enhancements && Array.isArray(data.enhancements)) {
          data.enhancements.forEach((enhancement) => {
            enhancements.push({
              id: `${sessionId}_${enhancement.id}`,
              sessionId,
              structureId: data.structureId,
              originalUrl: data.originalScreenshotUrl,
              enhancedUrl: enhancement.enhancedUrl,
              style: enhancement.style,
              prompt: enhancement.prompt,
              enhancementType: enhancement.enhancementType,
              createdAt: (() => {
                // Handle different date formats
                if (enhancement.createdAt) {
                  if (typeof enhancement.createdAt.toDate === 'function') {
                    return enhancement.createdAt.toDate();
                  } else if (enhancement.createdAt instanceof Date) {
                    return enhancement.createdAt;
                  } else if (typeof enhancement.createdAt === 'string') {
                    return new Date(enhancement.createdAt);
                  }
                }
                if (data.createdAt) {
                  if (typeof data.createdAt.toDate === 'function') {
                    return data.createdAt.toDate();
                  } else if (data.createdAt instanceof Date) {
                    return data.createdAt;
                  } else if (typeof data.createdAt === 'string') {
                    return new Date(data.createdAt);
                  }
                }
                return new Date();
              })(),
            });
          });
        }
      });

    // Each chunk comes back newest-session first; a merge of several needs
    // ordering again.
    return chunks.length > 1
      ? enhancements.sort((a, b) => b.createdAt - a.createdAt)
      : enhancements;
  } catch (error) {
    console.error('Error fetching image enhancements:', error);
    throw error;
  }
};

/**
 * Get all rooms for multiple structures with structure metadata
 */
export const getRoomsForMultipleStructures = async (structureIds) => {
  try {
    if (!structureIds || structureIds.length === 0) {
      return [];
    }

    // One `==` query per structure instead of a single `in` query. The
    // `rooms` read rule authorizes each room via dependent get() calls
    // (structure -> project -> user), and Firestore caps get()/exists() at
    // 10 per query request. An `in` query is evaluated against every
    // candidate value, so a project with more than ~3 structures exceeds the
    // cap and the whole query is denied with "insufficient permissions".
    const roomsCollection = collection(db, 'rooms');
    const snapshots = await Promise.all(
      structureIds.map((structureId) =>
        getDocs(query(roomsCollection, where('structureId', '==', structureId)))
      )
    );

    const rooms = snapshots.flatMap((snapshot) =>
      snapshot.docs.map((doc) => ({
        ...doc.data(),
        id: doc.id,
      }))
    );

    // Sort by timestamp in JavaScript instead of in the query
    rooms.sort((a, b) => {
      // Use timestamp field, falling back to createdAt if it doesn't exist
      const aTimestamp = a.timestamp || a.createdAt;
      const bTimestamp = b.timestamp || b.createdAt;

      const aDate = aTimestamp?.toDate
        ? aTimestamp.toDate()
        : new Date(aTimestamp || 0);
      const bDate = bTimestamp?.toDate
        ? bTimestamp.toDate()
        : new Date(bTimestamp || 0);
      return bDate - aDate; // Descending order (newest first)
    });

    return rooms;
  } catch (error) {
    console.error('Error fetching rooms for structures:', error);
    throw error;
  }
};

// Pure; in shared-lib so a Cloud Function assembles the same shape.
export { assembleStructureWithRooms };

/**
 * Get structure with its rooms from the new collections (no JSON loading)
 */
export const getStructureWithRooms = async (
  structureId,
  { fromServer = false, structureDoc = null } = {}
) => {
  try {
    // Get structure metadata. After a server-side write (e.g. checkpoint
    // restore) the cached read returns the pre-write structure, so callers
    // that just mutated Firestore pass `fromServer` to bypass the cache.
    //
    // The rooms query keys off `structureId`, which the caller already has, so
    // it does NOT depend on the structure doc. Issuing both together turns two
    // sequential round trips into one — the structure doc alone runs to 270 KB
    // on large scans, so the serialized version cost a full RTT for nothing.
    //
    // `structureDoc` lets a caller that ALREADY read `structures/{id}` hand the
    // data straight in. StructureDetail reads it up front to decide whether the
    // scan finished processing, and without this the viewer immediately read
    // the same (up to 270 KB) doc a second time. Never pass it after a
    // server-side write — use `fromServer` there instead.
    const structureRef = doc(db, 'structures', structureId);
    const [structureSnap, rooms] = await Promise.all([
      structureDoc
        ? null
        : fromServer
          ? getDocFromServer(structureRef)
          : getDoc(structureRef),
      getRoomsForStructure(structureId),
    ]);

    if (!structureDoc && !structureSnap.exists()) {
      throw new Error(`Structure ${structureId} not found in structures`);
    }

    const structureData = structureDoc
      ? { ...structureDoc, id: structureId }
      : structureFromSnap(structureSnap);

    return assembleStructureWithRooms(structureData, rooms);
  } catch (error) {
    console.error('Error getting structure with rooms:', error);
    throw error;
  }
};

/**
 * Live subscription to a structure + its rooms, assembled into the viewer
 * shape (same as `getStructureWithRooms`). This is the LIVE read seam the
 * homeowner viewer uses so it sees the contractor's ongoing edits.
 *
 * Listens to both the `structures/{id}` doc and the `rooms` query for that
 * structure. `onData` fires with the assembled structure once BOTH halves
 * have arrived at least once, and again on every subsequent change to either.
 *
 * @param {string} structureId
 * @param {(structureWithRooms: Object) => void} onData
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe (tears down both listeners)
 */
/**
 * Live subscription to the `structures/{id}` DOC ALONE — no rooms.
 *
 * For a host that already has the rooms from somewhere else and would
 * otherwise pay for the same `rooms` query twice. The homeowner design sandbox
 * is that host: it reads its rooms through `useRoomsSync` (which also
 * auto-SAVES them, which the visitor needs) and assembles the viewer shape
 * itself with `assembleStructureWithRooms`.
 *
 * @param {string} structureId
 * @param {(structureDoc: Object) => void} onData  `{ id, ...doc.data() }`
 * @param {(error: Error) => void} [onError]
 * @returns {() => void} unsubscribe
 */
export const subscribeStructureWithRooms = (structureId, onData, onError) => {
  let latestStructure = null;
  let latestRooms = null;

  // Emit only once both the structure doc and the rooms list have landed,
  // so the viewer never renders a half-assembled structure.
  const emitIfReady = () => {
    if (latestStructure && latestRooms) {
      onData(assembleStructureWithRooms(latestStructure, latestRooms));
    }
  };

  const handleError = (error) => {
    console.error('Error in structure live subscription:', error);
    if (onError) onError(error);
  };

  const structureRef = doc(db, 'structures', structureId);
  const unsubStructure = onSnapshot(
    structureRef,
    (snap) => {
      if (!snap.exists()) {
        handleError(new Error(`Structure ${structureId} not found`));
        return;
      }
      latestStructure = structureFromSnap(snap);
      emitIfReady();
    },
    handleError
  );

  const roomsQuery = query(
    collection(db, 'rooms'),
    where('structureId', '==', structureId)
  );
  const unsubRooms = onSnapshot(
    roomsQuery,
    (roomsSnapshot) => {
      latestRooms = roomsSnapshot.docs.map((roomDoc) =>
        normalizeRoomData({ ...roomDoc.data(), id: roomDoc.id })
      );
      emitIfReady();
    },
    handleError
  );

  return () => {
    unsubStructure();
    unsubRooms();
  };
};

/**
 * Convert structure to IFC using Cloud Function
 */
export const convertToIFCCloud = async (structureId, source = 'both') => {
  try {
    // Export structure to JSON (combines structure and rooms data)
    const jsonDataString = await exportStructureToJSON(structureId);
    const jsonData = JSON.parse(jsonDataString);

    console.log('Sending IFC conversion request:', {
      structureId,
      source,
      hasJsonData: !!jsonData,
      jsonDataKeys: jsonData ? Object.keys(jsonData) : null,
    });

    // Call Python cloud function via HTTP
    const functionUrl = `${process.env.REACT_APP_FUNCTIONS_URL}/convertToIFC`;
    if (!auth.currentUser) {
      throw new Error('Must be signed in to convert to IFC.');
    }
    const idToken = await auth.currentUser.getIdToken();

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        structureId,
        jsonData,
        source,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'IFC conversion failed');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Cloud IFC conversion failed:', error);
    throw error;
  }
};

/**
 * Convert structure to DXF using Cloud Function
 */
export const convertToDXFCloud = async (
  structureId,
  exportMode = '2d',
  source = 'both'
) => {
  try {
    // Export structure to JSON (combines structure and rooms data)
    const jsonDataString = await exportStructureToJSON(structureId);
    const jsonData = JSON.parse(jsonDataString);

    console.log('Sending DXF conversion request:', {
      structureId,
      mode: exportMode,
      source,
      hasJsonData: !!jsonData,
      jsonDataKeys: jsonData ? Object.keys(jsonData) : null,
    });

    // Call Python cloud function via HTTP
    const functionUrl = `${process.env.REACT_APP_FUNCTIONS_URL}/convertToDXF`;
    console.log('DXF Function URL:', functionUrl);
    if (!auth.currentUser) {
      throw new Error('Must be signed in to convert to DXF.');
    }
    const idToken = await auth.currentUser.getIdToken();

    const response = await fetch(functionUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${idToken}`,
      },
      body: JSON.stringify({
        structureId,
        jsonData,
        mode: exportMode,
        source,
      }),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'DXF conversion failed');
    }

    const result = await response.json();
    return result;
  } catch (error) {
    console.error('Cloud DXF conversion failed:', error);
    throw error;
  }
};

/**
 * Save IFC conversion metadata to Firestore
 */
export const saveIFCConversion = async (structureId, conversionData) => {
  try {
    const conversionRef = doc(db, 'ifcConversions', structureId);

    // Build the document data, excluding undefined fields
    const docData = {
      structureId,
      ifcUrl: conversionData.ifcUrl,
      storagePath: conversionData.storagePath,
      createdAt: serverTimestamp(),
      createdBy: auth.currentUser?.uid || 'anonymous',
    };

    // Only add optional fields if they exist
    if (conversionData.reportUrl) {
      docData.reportUrl = conversionData.reportUrl;
    }
    if (conversionData.reportPath) {
      docData.reportPath = conversionData.reportPath;
    }

    await setDoc(conversionRef, docData);

    return conversionRef.id;
  } catch (error) {
    console.error('Error saving IFC conversion:', error);
    throw error;
  }
};

/**
 * Get pre-transformed structure data
 */
const getPreTransformedStructure = async (structureId) => {
  try {
    const docRef = doc(db, 'structures', structureId);
    const docSnap = await getDoc(docRef);

    if (docSnap.exists()) {
      return structureFromSnap(docSnap);
    } else {
      throw new Error(`Structure ${structureId} not found`);
    }
  } catch (error) {
    console.error('Error fetching structure:', error);
    throw error;
  }
};

/**
 * Delete a structure and all its associated data
 */
export const deleteStructure = async (structureId) => {
  try {
    console.log('Deleting structure:', structureId);

    // Delete from structures collection
    const structureRef = doc(db, 'structures', structureId);
    await deleteDoc(structureRef);

    // Delete associated rooms
    const roomsQuery = query(
      collection(db, 'rooms'),
      where('structureId', '==', structureId)
    );
    const roomsSnapshot = await getDocs(roomsQuery);

    const deletePromises = [];
    roomsSnapshot.forEach((doc) => {
      deletePromises.push(deleteDoc(doc.ref));
    });

    // Delete from originalScans if it exists there
    try {
      const originalScanRef = doc(db, 'originalScans', structureId);
      const originalScanSnap = await getDoc(originalScanRef);
      if (originalScanSnap.exists()) {
        deletePromises.push(deleteDoc(originalScanRef));
      }
    } catch (error) {
      // Ignore if doesn't exist in originalScans
      console.log('No original scan to delete');
    }

    // Wait for all deletions to complete
    await Promise.all(deletePromises);

    console.log(
      `Successfully deleted structure ${structureId} and ${roomsSnapshot.size} associated rooms`
    );
    return { success: true, roomsDeleted: roomsSnapshot.size };
  } catch (error) {
    console.error('Error deleting structure:', error);
    throw error;
  }
};
